import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DateEx } from '../../../../functions/date-ex';
import { Action } from '../../../../models/core/action';
import { ToolbarButtonComponent } from '../../../core/buttons/toolbar-button.component';
import { MenuComponent } from '../../../core/panels/menu.component';

/**
 * Use as a placeholder until actual implementations are available.
 */
@Component({
  selector: 'app-month-view',
  imports: [CommonModule, ToolbarButtonComponent, MenuComponent],
  template: `
    <div
      class="tw-grid tw-grid-cols-7 tw-max-w-[360px] tw-gap-1 tw-justify-center tw-items-center tw-text-center "
    >
      <div class="tw-col-span-full tw-py-1 tw-border-b">
        <div class="tw-flex tw-items-center tw-justify-between tw-h-[36px]">
          @if (showNavigation) {
            <span class="tw-font-bold tw-text-[18px] tw-pl-1.5">
              {{ startDate | date: 'MMM y' }}
            </span>

            <div class="tw-text-right">
              <app-toolbar-button
                icon="fa-light fa-chevron-left"
                (click)="monthClick(navigationMonths * -1)"
                type="Small"
                ariaLabel="Previous month"
              />
              <app-toolbar-button
                icon="fa-light fa-chevron-right"
                (click)="monthClick(navigationMonths)"
                type="Small"
                ariaLabel="Next month"
              />
              <app-toolbar-button
                icon="fa-light fa-calendar-day"
                (click)="toggleMenu($event)"
                type="Small"
                ariaLabel="Jump to date"
              />
              @if (menuVersion) {
                <app-menu
                  [items]="menuActionsArray"
                  #menu
                  (click)="onMenuDateClick($event.data)"
                />
              }
            </div>
          } @else {
            <span class="tw-font-bold tw-text-[18px] tw-pl-1.5">
              {{ startDate | date: 'MMM y' }}
            </span>
            <span></span>
          }
        </div>
      </div>
      @for (dow of daysOfWeek; track dow) {
        <div class="dow tw-flex tw-items-center tw-justify-center ">
          {{ dow }}
        </div>
      }
      @for (day of days; track day.date) {
        <div class="tw-flex tw-items-center tw-justify-center ">
          @if (day.label) {
            <div
              class="tw-font-bold tw-flex tw-items-center tw-justify-center circle"
              [tabindex]="0"
              [class.circle-selected]="day === selectedDay"
              [ngClass]="day.getClasses()"
              (focus)="onDateChanged(day)"
            >
              <span class="">{{ day.label }}</span>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: `
    .dow {
      font-weight: bold;
      color: var(--sed-black-60-color);
      height: 28px;
    }
    .day {
      font-weight: bold;
      font-size: 14px;
      color: var(--sed-black-80-color);
    }
    .circle-hidden {
      border-radius: 50%;
      width: 28px;
      height: 28px;
    }
    .circle {
      border-radius: 50%;
      width: 28px;
      height: 28px;
      cursor: pointer;
    }
    .circle:focus {
      outline: 2px solid var(--sed-engaged-black-color);
      outline-offset: 3px;
    }

    .circle-selected {
      outline: 2px solid var(--sed-engaged-black-color);
      outline-offset: 3px;
    }
  `,
})
export class MonthViewComponent implements OnInit {
  @ViewChild('menu', { static: false })
  menu: MenuComponent;

  /**
   * The month to display
   */
  @Input()
  get month(): Date {
    return this._month;
  }

  set month(value: Date) {
    this._month = value;
    this.calculateDays();
  }

  private _month: Date;

  /**
   * The selected date
   */
  @Input()
  selectedDate: Date;

  /**
   * Populates toolbar's event button with quick links to dates
   */
  @Input()
  set dateMenu(value: Day[]) {
    // Always update when new value is provided to ensure menu stays in sync
    if (value) {
      this._dateMenu = value;
      // Regenerate menu actions immediately when dateMenu changes
      this.updateMenuActions();
    }
  }

  get dateMenu(): Day[] {
    return this._dateMenu;
  }

  private _dateMenu: Day[] = [];

  @Input()
  showNavigation = true;

  @Input()
  navigationMonths = 1;

  @Input()
  isVisible = true;

  @Output()
  monthChanged = new EventEmitter<Day[]>();

  @Output()
  dateChanged = new EventEmitter<Day>();

  public days: Day[] = [];

  protected daysOfWeek: string[] = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  protected startDate: Date;

  protected endDate: Date;

  protected selectedDay: Day;

  // Menu actions stored as property, updated only when dateMenu changes
  protected menuActionsArray: Action[] = [];

  // Version counter to force menu recreation
  protected menuVersion = 1;

  ngOnInit(): void {
    if (this.selectedDate) {
      this.month = this.selectedDate;
      this.setDate(this.selectedDate);
      this.selectDate();
    }
    // Initialize menu actions from dateMenu
    this.updateMenuActions();
  }

  onDateChanged(day: Day) {
    this.selectedDate = day.date;
    this.selectedDay = day;
    this.dateChanged.emit(day);
  }

  toggleMenu(event: Event): void {
    if (this.menu) {
      this.menu.toggle(event);
    }
  }

  onMenuDateClick(day: Day) {
    if (!day) {
      return;
    }

    // Check if the selected date is in a different month
    const selectedMonth = day.date.getMonth();
    const selectedYear = day.date.getFullYear();
    const currentMonth = this.month.getMonth();
    const currentYear = this.month.getFullYear();

    // If different month/year, navigate to that month first
    if (selectedMonth !== currentMonth || selectedYear !== currentYear) {
      this.month = new Date(selectedYear, selectedMonth, 1);
    }

    // Find the corresponding day in the current month's days, or use the menu day
    const monthDay = this.days.find((d) => DateEx.dateEquals(d.date, day.date));
    const dayToSelect = monthDay || day;
    this.onDateChanged(dayToSelect);
  }

  private updateMenuActions(): void {
    // Force menu recreation by toggling version
    this.menuVersion = 0;

    // Use setTimeout to ensure menu is destroyed before recreating
    setTimeout(() => {
      // Regenerate menu actions from current dateMenu
      this.menuActionsArray = [];
      if (this.dateMenu && this.dateMenu.length > 0) {
        for (const day of this.dateMenu) {
          const isDisabled = (day as any).disabled === true;
          this.menuActionsArray.push(
            new Action({
              label: day.label,
              data: day,
              disabled: isDisabled,
            }),
          );
        }
      }
      // Recreate menu with new items
      this.menuVersion = Date.now();
    }, 0);
  }

  calculateDays() {
    if (!this.month || !this.isVisible) {
      return;
    }
    const month = this.month.getMonth();
    const year = this.month.getFullYear();
    let date = new Date(year, month, 1);
    this.startDate = new Date(date);
    const endDate = new Date(date);
    endDate.setMonth(month + 1);

    // this is calculating offset of first week
    date.setDate(date.getDate() - 1);
    while (date.getDay() > 0) {
      date.setDate(date.getDate() - 1);
    }

    const monthDays = [];
    this.days = [];
    const today = new Date().toDateString();
    while (date < endDate) {
      if (month === date.getMonth()) {
        const day = new Day(date, `${date.getDate()}`);
        if (day.date.toDateString() === today) {
          day.color ??= 'LightGrey';
        }

        this.days.push(day);
        monthDays.push(day);
      } else {
        this.days.push(new Day(date, ''));
      }

      date = new Date(date);
      date.setDate(date.getDate() + 1);
    }

    this.monthChanged.emit(monthDays);
  }

  selectDate() {
    this.selectedDay = this.days.find((x) =>
      DateEx.dateEquals(x.date, this.selectedDate),
    );
    if (this.selectedDay) {
      this.dateChanged.emit(this.selectedDay);
    }
  }

  monthClick(months: number) {
    this.month = DateEx.addMonths(this.startDate, months);
    this.calculateDays();
  }

  setDate(date: Date) {
    this.selectedDate = date;
    this.selectedDay = date
      ? this.days.find((x) => DateEx.dateEquals(x.date, date))
      : null;
  }
}

export class Day {
  public data: any;

  public color: DayColor;

  constructor(
    public date: Date,
    public label: string,
  ) {}

  public getClasses(): string {
    switch (this.color) {
      case 'Green':
        return 'tw-bg-[var(--sed-status-green-color)] tw-text-[var(--sed-warm-white-color)]';
      case 'Red':
        return 'tw-bg-[var(--sed-status-red-color)] tw-text-[var(--sed-warm-white-color)]';
      case 'Yellow':
        return 'tw-bg-[var(--sed-status-yellow-color)] tw-text-[var(--sed-dynamic-black-color]';
      case 'LightGrey':
        return 'tw-bg-[var(--sed-open-grey-color)] tw-text-[var(--sed-dynamic-black-color]';
      case 'Purple':
        return 'tw-bg-[var(--sed-purple-40-color)] tw-text-[var(--sed-dynamic-black-color]';
      case 'DarkGrey':
        return 'tw-bg-[var(--sed-black-80-color)] tw-text-[var(--sed-warm-white-color)]';
      case 'Teal':
        return 'tw-bg-[var(--sed-data-teal-color)] tw-text-[var(--sed-dynamic-black-color)]';
    }
  }
}

export type DayColor =
  | 'Green'
  | 'Red'
  | 'Yellow'
  | 'LightGrey'
  | 'DarkGrey'
  | 'Purple'
  | 'Teal';
